// src/modules/userHome/UserHome.tsx
import React, { useEffect, useMemo, useState } from "react";
import { GetUserKpisParams } from "./UserHomeApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faSync } from "@fortawesome/free-solid-svg-icons";

/** FormMode is mostly for consistency with your pattern */
export type FormMode = "view";

interface Props {
  mode?: FormMode;
  /** These are kept for backward compatibility but will be overridden by localStorage if present */
  initialTenantId?: string;
  initialUserId?: string;
  initialLookaheadDays?: number;
  initialLookbackWeeks?: number;
  onApply: (p: Omit<GetUserKpisParams, "signal">) => void;
  onBack?: () => void;
  screenTitle?: string;
}

/** Safely read user/tenant info from localStorage.
 *  Supports either:
 *  - localStorage["user"] = JSON.stringify({ tenantId, tenantName, userId, userName })
 *  - or individual keys: tenantId, tenantName, userId, userName
 */
function readAuthFromLocalStorage() {
  let tenantId: string | undefined;
  let tenantName: string | undefined;
  let userId: string | undefined;
  let userName: string | undefined;

  try {
    const raw = localStorage.getItem("user");
    if (raw) {
      const user = JSON.parse(raw);
      tenantId = user?.tenantId ?? user?.tenant_id ?? undefined;
      tenantName = user?.tenantName ?? user?.tenant_name ?? undefined;
      userId = user?.userId ?? user?.user_id ?? undefined;
      userName = user?.username ?? user?.user_name ?? undefined;
    }
  } catch {
    // ignore JSON parse errors and fall back to individual keys
  }

  // Fallbacks if not present in "user"
  tenantId = tenantId ?? localStorage.getItem("tenantId") ?? undefined;
  tenantName = tenantName ?? localStorage.getItem("tenantName") ?? undefined;
  userId = userId ?? localStorage.getItem("userId") ?? undefined;
  userName = userName ?? localStorage.getItem("username") ?? undefined;

  return { tenantId, tenantName, userId, userName };
}

export const UserHomeForm: React.FC<Props> = ({
  mode = "view",
  initialTenantId,
  initialUserId,
  initialLookaheadDays = 30,
  initialLookbackWeeks = 12,
  onApply,
  onBack,
  screenTitle = "User Home Dashboard",
}) => {
  const { tenantId: lsTenantId, tenantName: lsTenantName, userId: lsUserId, userName: lsUserName } =
    readAuthFromLocalStorage();

  // Non-editable IDs (read from localStorage or fallback to props)
  const tenantId = (lsTenantId ?? initialTenantId ?? "").trim();
  const userId = (lsUserId ?? initialUserId ?? "").trim();

  // Non-editable display names (from localStorage; fallback to IDs if names missing)
  const tenantName = (lsTenantName ?? "").trim() || tenantId;
  const userName = (lsUserName ?? "").trim() || userId;

  const [lookaheadDays, setLookaheadDays] = useState(initialLookaheadDays);
  const [lookbackWeeks, setLookbackWeeks] = useState(initialLookbackWeeks);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const canSubmit = useMemo(() => {
    return (
      !!tenantId &&
      !!userId &&
      Number.isFinite(lookaheadDays) &&
      Number.isFinite(lookbackWeeks) &&
      lookaheadDays > 0 &&
      lookbackWeeks > 0
    );
  }, [tenantId, userId, lookaheadDays, lookbackWeeks]);

  useEffect(() => {
    // On first mount, auto-apply with values
    if (tenantId && userId) {
      onApply({ tenantId, userId, lookaheadDays, lookbackWeeks });
    } else {
      // surface errors if IDs are missing
      validate(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function validate(skipNumeric = false): boolean {
    const e: Record<string, string> = {};
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    if (!tenantId) e.tenantId = "Tenant ID not found in localStorage.";
    else if (!uuidRegex.test(tenantId)) e.tenantId = "Tenant ID in localStorage is not a valid UUID.";

    if (!userId) e.userId = "User ID not found in localStorage.";
    else if (!uuidRegex.test(userId)) e.userId = "User ID in localStorage is not a valid UUID.";

    if (!skipNumeric) {
      if (!lookaheadDays || lookaheadDays <= 0) e.lookaheadDays = "Must be > 0.";
      if (!lookbackWeeks || lookbackWeeks <= 0) e.lookbackWeeks = "Must be > 0.";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    onApply({ tenantId, userId, lookaheadDays, lookbackWeeks });
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="btn btn-sm rounded-xl border px-3 py-1 hover:bg-slate-50"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
              Back
            </button>
          )}
          <h1 className="text-xl font-semibold">{screenTitle}</h1>
        </div>
      </div>

      {/* Optional global error banner when IDs are missing/invalid */}
      {(errors.tenantId || errors.userId) && (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          <p className="font-medium">Configuration issue</p>
          <ul className="list-disc ml-5 text-sm">
            {errors.tenantId && <li>{errors.tenantId}</li>}
            {errors.userId && <li>{errors.userId}</li>}
          </ul>
        </div>
      )}

      <form
        onSubmit={submit}
        className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4 rounded-2xl border bg-white shadow-sm"
      >
        {/* Tenant Name (read-only) */}
        <div className="col-span-2">
          <label className="text-sm font-medium">Tenant Name</label>
          <input
            className="mt-1 w-full rounded-xl border px-3 py-2 bg-slate-50 text-slate-700"
            value={tenantName}
            readOnly
            disabled
          />
          {/* Hidden display of ID if you want to debug:
          <p className="text-[11px] text-slate-500 mt-1">Tenant ID: {tenantId}</p> */}
          {errors.tenantId && <p className="text-red-600 text-xs mt-1">{errors.tenantId}</p>}
        </div>

        {/* User Name (read-only) */}
        <div className="col-span-2">
          <label className="text-sm font-medium">User Name</label>
          <input
            className="mt-1 w-full rounded-xl border px-3 py-2 bg-slate-50 text-slate-700"
            value={userName}
            readOnly
            disabled
          />
          {/* Hidden display of ID if you want to debug:
          <p className="text-[11px] text-slate-500 mt-1">User ID: {userId}</p> */}
          {errors.userId && <p className="text-red-600 text-xs mt-1">{errors.userId}</p>}
        </div>

        {/* Lookahead */}
        <div className="col-span-1">
          <label className="text-sm font-medium">Lookahead (days)</label>
          <input
            type="number"
            min={1}
            className="mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring"
            value={lookaheadDays}
            onChange={(e) => setLookaheadDays(parseInt(e.target.value || "0", 10))}
          />
          {errors.lookaheadDays && <p className="text-red-600 text-xs mt-1">{errors.lookaheadDays}</p>}
        </div>

        {/* Lookback */}
        <div className="col-span-1">
          <label className="text-sm font-medium">Lookback (weeks)</label>
          <input
            type="number"
            min={1}
            className="mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring"
            value={lookbackWeeks}
            onChange={(e) => setLookbackWeeks(parseInt(e.target.value || "0", 10))}
          />
          {errors.lookbackWeeks && (
            <p className="text-red-600 text-xs mt-1">{errors.lookbackWeeks}</p>
          )}
        </div>

        {/* Apply */}
        <div className="col-span-1 flex items-end">
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-xl border px-4 py-2 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            title="Refresh"
          >
            <FontAwesomeIcon icon={faSync} className="mr-2" />
            Apply
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserHomeForm;